/**
 * Level 3, Lessons 14–18: area ("the array you can stand on"), perimeter ("the walk around the edge"),
 * polygons and the quadrilateral family ("check the rules, not the look"), shapes cut into equal parts, bar graphs.
 * Area facts follow Meliesa's array rule: the count in each column, counted (columns) times — 4 rows, 6 columns is
 * "four, counted six times: 4 × 6". Either order is accepted for the area itself (turning never changes it).
 */
import { int, pick } from "../core";
import type { Item, Skill } from "../types";
import { barGraph, rectLabeled, SHAPES, sqGrid } from "../visuals";

const UNITS = [["ft", "feet"], ["in", "inches"], ["m", "meters"], ["cm", "centimeters"]] as const;

/* ---------- L3.14 Area ---------- */
export function areaGridItem(cols: number, rows: number): Item {
  const p = cols * rows;
  return {
    id: `area-grid:${cols}x${rows}`,
    skill: "area-grid",
    prompt: "Find the area. Write it as a times fact — count by the column.",
    visual: sqGrid(cols, rows),
    input: { kind: "fields", fields: [{ width: 2 }, { label: "×", width: 2 }, { label: "=", width: 3, suffix: "square units" }] },
    answer: `${rows}|${cols}|${p}`,
    alsoAccept: [`${cols}|${rows}|${p}`],
    diagnose: (raw) => {
      const z = Number(raw.split("|")[2]);
      if (z === 2 * (cols + rows) && z !== p) return { code: "area-perimeter", hint: "That's the walk around the edge. Area counts the squares inside." };
      if (z === cols + rows && z !== p) return { code: "array-added", hint: "Times means how many times you counted. Count by the column." };
      return null;
    },
    hints: ["It's an array. How many squares in each column? How many columns?", `${rows} in each column, ${cols} columns: count by ${rows}s, ${cols} times.`, `${rows}, counted ${cols} times: ${rows} × ${cols} = ${p} square units.`],
  };
}
export const areaGrid: Skill = { id: "area-grid", name: "Area of a tiled rectangle", gen: (rng) => areaGridItem(int(rng, 3, 9), int(rng, 2, 6)) };

export function areaSidesItem(l: number, w: number, u: (typeof UNITS)[number]): Item {
  const p = l * w;
  return {
    id: `area-sides:${l}x${w}${u[0]}`,
    skill: "area-sides",
    prompt: `A rectangle is <b>${l} ${u[1]}</b> long and <b>${w} ${u[1]}</b> wide. No tiles — what is its area?`,
    visual: rectLabeled(l, w, u[0], false),
    input: { kind: "number", label: `square ${u[1]}` },
    answer: String(p),
    diagnose: (raw) => {
      const n = Number(raw.trim());
      if (n === 2 * (l + w) && n !== p) return { code: "area-perimeter", hint: "That's the walk around the edge — the perimeter. Area counts the squares inside." };
      if (n === l + w && n !== p) return { code: "array-added", hint: "Length times width IS the array fact." };
      return null;
    },
    hints: ["Picture the tiles: how many columns, how many in each?", "Length times width IS the array fact.", `${l} × ${w} = ${p} square ${u[1]}.`],
  };
}
export const areaSides: Skill = { id: "area-sides", name: "Area from length and width", gen: (rng) => areaSidesItem(int(rng, 3, 12), int(rng, 2, 9), pick(rng, [...UNITS])) };

export function areaBackItem(side: number, other: number): Item {
  const a = side * other;
  return {
    id: `area-backwards:${a}/${side}`,
    skill: "area-backwards",
    prompt: `Backwards! A rectangle has an area of <b>${a} square units</b>. One side is <b>${side} units</b>. How long is the other side?`,
    input: { kind: "number", label: "units" },
    answer: String(other),
    diagnose: (raw) => {
      const n = Number(raw.trim());
      if (n === a - side && n !== other) return { code: "missing-subtracted", hint: "Count your way to the answer — the number of counts is the missing side." };
      if (n === a) return { code: "missing-gave-product", hint: "The area is already there. How many times do you count by the side to reach it?" };
      return null;
    },
    hints: [`${side} × ? = ${a}.`, `Count by ${side}s to ${a} — jump to the nearest anchor first. The answer is how many times you counted.`, `${side} × ${other} = ${a}: the other side is ${other} units.`],
  };
}
export const areaBack: Skill = { id: "area-backwards", name: "Area backwards — find the missing side", gen: (rng) => areaBackItem(pick(rng, [3, 4, 6, 7, 8, 9]), int(rng, 2, 9)) };

/* ---------- L3.15 Perimeter ---------- */
export function perimRectItem(l: number, w: number, u: (typeof UNITS)[number], allLabels: boolean): Item {
  const P = 2 * (l + w);
  return {
    id: `perimeter-rect:${l}x${w}${u[0]}:${allLabels ? 4 : 2}`,
    skill: "perimeter-rect",
    prompt: allLabels ? "Walk the edge. What is the perimeter?" : "Only two sides are labeled. Walk the whole edge — what is the perimeter?",
    visual: rectLabeled(l, w, u[0], allLabels),
    input: { kind: "number", label: u[1] },
    answer: String(P),
    diagnose: (raw) => {
      const n = Number(raw.trim());
      if (n === l * w && n !== P) return { code: "perim-area", hint: "That counts the squares inside — the area. Perimeter is the walk around the edge." };
      if (n === l + w && n !== P) return { code: "perim-two-sides", hint: "That's only two sides. Walk all the way around, back to where you started." };
      return null;
    },
    hints: [allLabels ? "Add every side, all the way around." : "A rectangle's opposite sides match. What are the two sides you can't see labeled?", `${l} + ${w} + ${l} + ${w}.`, `${l} + ${w} + ${l} + ${w} = ${P} ${u[1]}.`],
  };
}
export const perimRect: Skill = { id: "perimeter-rect", name: "Perimeter of a rectangle", gen: (rng, level) => perimRectItem(int(rng, 3, 12), int(rng, 2, 9), pick(rng, [...UNITS]), level < 1) };

const POLY: [number, string][] = [[3, "equal-sided triangle"], [4, "square"], [5, "pentagon"], [6, "hexagon"], [8, "octagon"]];
export function perimEqualItem(n: number, len: number, name: string, u: (typeof UNITS)[number]): Item {
  const P = n * len;
  return {
    id: `perimeter-equal:${n}x${len}`,
    skill: "perimeter-equal",
    prompt: `${/^[aeiou]/.test(name) ? "An" : "A"} <b>${name}</b> (${n} sides), each side <b>${len} ${u[1]}</b>. What is the perimeter?`,
    input: { kind: "number", label: u[1] },
    answer: String(P),
    diagnose: (raw) => {
      const n2 = Number(raw.trim());
      if (n2 === n + len && n2 !== P) return { code: "array-added", hint: `Equal sides turn adding into multiplying: ${n} sides, each ${len}.` };
      return null;
    },
    hints: [`You could add ${Array(n).fill(len).join(" + ")} — what faster question could you ask?`, `Equal sides turn adding into multiplying: sides times length.`, `${n} × ${len} = ${P} ${u[1]}.`],
  };
}
export const perimEqual: Skill = { id: "perimeter-equal", name: "Perimeter of an equal-sided shape", gen: (rng) => { const [n, name] = pick(rng, POLY); return perimEqualItem(n, int(rng, 2, 12), name, pick(rng, [...UNITS])); } };

export function perimBackItem(n: number, name: string, len: number): Item {
  const P = n * len;
  return {
    id: `perimeter-backwards:${P}/${n}`,
    skill: "perimeter-backwards",
    prompt: `Backwards! ${/^[aeiou]/.test(name) ? "An" : "A"} <b>${name}</b> (${n} equal sides) has a perimeter of <b>${P}</b>. How long is one side?`,
    input: { kind: "number" },
    answer: String(len),
    diagnose: (raw) => (Number(raw.trim()) === P - n && P - n !== len ? { code: "missing-subtracted", hint: `Count your way: how many ${n}s make ${P}?` } : null),
    hints: [`${n} sides, all the same: ${n} × ? = ${P}.`, `Perimeter backwards is division — ${P} ÷ ${n} — so count by ${n}s up to ${P}: how many counts?`, `${n} × ${len} = ${P}: one side is ${len}.`],
  };
}
export const perimBack: Skill = { id: "perimeter-backwards", name: "Perimeter backwards — find one side", gen: (rng) => { const [n, name] = pick(rng, POLY); return perimBackItem(n, name, int(rng, 2, 12)); } };

/* ---------- L3.16 Polygons: name it by counting the sides ---------- */
const NAMES = ["triangle", "quadrilateral", "pentagon", "hexagon", "octagon"];
const BY_SIDES: Record<number, string> = { 3: "triangle", 4: "quadrilateral", 5: "pentagon", 6: "hexagon", 8: "octagon" };
const PICS: [keyof typeof SHAPES, number][] = [["triangle", 3], ["scalene", 3], ["square", 4], ["rectangle", 4], ["trapezoid", 4], ["rhombus", 4], ["parallelogram", 4], ["quadrilateral", 4], ["pentagon", 5], ["hexagon", 6], ["octagon", 8]];
export function polygonNameItem(shape: keyof typeof SHAPES, sides: number): Item {
  const ans = BY_SIDES[sides]!;
  return {
    id: `polygon-name:${shape}`,
    skill: "polygon-name",
    prompt: "Name this polygon by counting its sides.",
    visual: SHAPES[shape],
    input: { kind: "choice", options: NAMES },
    answer: ans,
    diagnose: (raw) => {
      if (sides === 4 && raw !== ans && ["square", "rectangle", "trapezoid", "rhombus"].includes(raw)) return null;
      const said = NAMES.indexOf(raw);
      if (said >= 0 && raw !== ans) return { code: "polygon-miscount", hint: "Touch each side as you count it. Start at one corner and go all the way around." };
      return null;
    },
    hints: ["Count the straight sides.", `Touch each side as you count — it has ${sides}.`, `${sides} sides: ${ans}.`],
  };
}
export const polygonName: Skill = { id: "polygon-name", name: "Name a polygon by counting its sides", gen: (rng) => { const [s, n] = pick(rng, PICS); return polygonNameItem(s, n); } };

type Rule = { q: string; a: "true" | "false"; why: string; hint: string };
const RULES: Rule[] = [
  { q: "Every square is a rectangle.", a: "true", why: "A square passes every rectangle test: four sides, square corners. It is a rectangle with equal sides.", hint: "Check the rectangle rules: four sides, four square corners. Does a square pass?" },
  { q: "Every rectangle is a square.", a: "false", why: "A square also needs four equal sides — a long rectangle doesn't have them.", hint: "A square needs four equal sides. Does every rectangle have them?" },
  { q: "A trapezoid is a quadrilateral.", a: "true", why: "Four straight sides — that's all a quadrilateral needs.", hint: "Count its sides." },
  { q: "A rhombus has four equal sides.", a: "true", why: "Four equal sides is what makes it a rhombus.", hint: "What makes a rhombus a rhombus?" },
  { q: "Every rhombus has square corners.", a: "false", why: "A rhombus can lean. Only the square has equal sides AND square corners.", hint: "Picture a rhombus that leans. Square corners?" },
  { q: "A square is a rhombus.", a: "true", why: "Four equal sides passes the rhombus test.", hint: "Check the rhombus rule: four equal sides." },
  { q: "A circle is a polygon.", a: "false", why: "No straight sides — nothing to count.", hint: "A polygon is made only of straight sides." },
  { q: "A triangle can have sides of three different lengths.", a: "true", why: "Three straight sides is all it takes.", hint: "What does a triangle need?" },
];
export function quadRuleItem(r: Rule): Item {
  return {
    id: `quad-rule:${r.q}`,
    skill: "quad-rule",
    prompt: `True or false? <b>${r.q}</b>`,
    input: { kind: "choice", options: ["true", "false"] },
    answer: r.a,
    hints: [r.hint, "Check the rules, not the look.", r.why],
    reveal: r.why,
  };
}
export const quadRule: Skill = { id: "quad-rule", name: "The quadrilateral family — check the rules", gen: (rng) => quadRuleItem(pick(rng, RULES)) };

/* ---------- L3.18 Bar graphs ---------- */
const SETS = [
  { title: "Pets in Room 12", labels: ["Dogs", "Cats", "Fish", "Birds"], what: "pets" },
  { title: "Books read this month", labels: ["Ava", "Ben", "Cy", "Dee"], what: "books" },
  { title: "Goals scored this season", labels: ["Sam", "Lia", "Kai", "Mo"], what: "goals" },
  { title: "Favorite seasons", labels: ["Summer", "Fall", "Winter", "Spring"], what: "votes" },
];
export function barItem(set: number, values: number[], ask: "read" | "more" | "all", i = 0, j = 1, step = 1): Item {
  const S = SETS[set]!;
  const bars = S.labels.map((label, k) => ({ label, value: values[k]! }));
  const L = S.labels;
  let prompt = "", ans = 0;
  const hints: string[] = [];
  if (ask === "read") { prompt = `How many ${S.what} for <b>${L[i]}</b>?`; ans = values[i]!; hints.push(`Follow the top of ${L[i]}'s bar across to the scale.`, step === 2 ? "Careful: each line counts by two. Halfway between two lines is one more." : "Read the number on the line where the bar stops.", `${L[i]}'s bar stops at ${ans}.`); }
  if (ask === "more") { const [hi, lo] = values[i]! >= values[j]! ? [i, j] : [j, i]; prompt = `How many more ${S.what} for <b>${L[hi]}</b> than for <b>${L[lo]}</b>?`; ans = values[hi]! - values[lo]!; hints.push(`Read both bars first: ${L[hi]} and ${L[lo]}.`, "That word \"more\" — which operation is hiding in it? \"How many more\" compares by subtracting.", `${values[hi]} − ${values[lo]} = ${ans}.`); i = hi; j = lo; }
  if (ask === "all") { prompt = `How many ${S.what} in all?`; ans = values.reduce((a, b) => a + b, 0); hints.push("Read every bar.", "\"In all\" puts them together: add every bar.", `${values.join(" + ")} = ${ans}.`); }
  return {
    id: `bar-graph:${set}:${values.join(",")}:${ask}:${i}:${j}`,
    skill: "bar-graph",
    prompt: `<b>${S.title}</b>${step === 2 ? " (each line counts by 2)" : ""}. ${prompt}`,
    visual: barGraph(bars, 8, step),
    input: { kind: "number" },
    answer: String(ans),
    diagnose: (raw) => {
      const n = Number(raw.trim());
      if (ask === "more" && n === values[i]! + values[j]!) return { code: "word-wrong-op", hint: "\"How many more\" compares — it takes away, it doesn't put together." };
      if (ask === "more" && (n === values[i] || n === values[j]) && n !== ans) return { code: "graph-read-not-compare", hint: "That's one bar. How many more is the difference between the two." };
      return null;
    },
    hints,
  };
}
export const barGraphSkill: Skill = {
  id: "bar-graph",
  name: "Read a bar graph — how many, how many more, in all",
  gen: (rng, level) => {
    const set = int(rng, 0, SETS.length - 1);
    const vals = [0, 0, 0, 0].map(() => int(rng, 1, 8));
    const i = int(rng, 0, 3);
    let j = int(rng, 0, 3);
    if (j === i) j = (i + 1) % 4;
    if (vals[j] === vals[i]) vals[j] = vals[i]! > 1 ? vals[i]! - 1 : 2;
    const ask = level < 1 ? pick(rng, ["read", "more"] as const) : pick(rng, ["read", "more", "all"] as const);
    return barItem(set, vals, ask, i, j, level >= 2 ? 2 : 1);
  },
};

export const LEVEL3_L14TO18_SKILLS: Skill[] = [areaGrid, areaSides, areaBack, perimRect, perimEqual, perimBack, polygonName, quadRule, barGraphSkill];
